import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";

const ROLES = ["super_admin", "operations", "business_development", "marketing", "finance", "venue_manager"];

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "operations", phone: "", venue_ids: [] });

  const load = async () => {
    const [u, v] = await Promise.all([api.get("/users"), api.get("/venues")]);
    setUsers(u.data); setVenues(v.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.email || !f.password || !f.name) return toast.error("Name, email, password required");
    try { await api.post("/users", f); toast.success("User created"); setOpen(false); setF({ name: "", email: "", password: "", role: "operations", phone: "", venue_ids: [] }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Team</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Team & Roles</h1>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-user-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add User</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Team Member</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name</Label><Input data-testid="user-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
                <div><Label>Password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
                <div><Label>Role</Label>
                  <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="user-save-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-slate-200"><Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Phone</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell className="font-medium">{u.name}</TableCell>
              <TableCell>{u.email}</TableCell>
              <TableCell><Badge className="bg-blue-50 text-blue-700 border-blue-200 border capitalize">{(u.role || "").replace("_", " ")}</Badge></TableCell>
              <TableCell>{u.phone || "—"}</TableCell>
              <TableCell><Badge className={u.active !== false ? "bg-emerald-100 text-emerald-800 border-emerald-200 border" : "bg-slate-100 text-slate-700 border-slate-200 border"}>{u.active !== false ? "Active" : "Inactive"}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Card>
    </div>
  );
}
